import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import FullscreenImageModal from './FullscreenImageModal';

// Rarity translation map
const RARITY_TRANSLATIONS = {
  'common': 'comune',
  'rare': 'rara',
  'Common': 'Comune',
  'Rare': 'Rara',
  'rainbow': 'arcobaleno',
  'Rainbow': 'Arcobaleno',
  'legendary': 'leggendaria',
  'Legendary': 'Leggendaria',
  'epic': 'epica',
  'Epic': 'Epica',
  'uncommon': 'non comune',
  'Uncommon': 'Non Comune'
};

const translateRarity = (rarity) => {
  return RARITY_TRANSLATIONS[rarity] || rarity;
};

function Social({ user, token }) {
  const [rankings, setRankings] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCards, setUserCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('cards');
  const [error, setError] = useState('');
  const [fullscreenImage, setFullscreenImage] = useState({ isOpen: false, imageUrl: '', cardName: '' });

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/rankings`);
      setRankings(response.data);
    } catch (error) {
      setError('Errore nel caricamento delle classifiche');
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCards = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/cards`);
      setUserCards(response.data.cards);
      setSelectedUser(response.data.user);
    } catch (error) {
      setError('Errore nel caricamento delle carte dell\'utente');
      console.error('Error fetching user cards:', error);
    }
  };

  const sortedRankings = [...rankings].sort((a, b) => {
    if (sortBy === 'cards') {
      return b.cards_owned - a.cards_owned || b.patapizze - a.patapizze;
    } else if (sortBy === 'patapizze') {
      return b.patapizze - a.patapizze || b.cards_owned - a.cards_owned;
    } else if (sortBy === 'spent') {
      return b.total_spent - a.total_spent || b.cards_owned - a.cards_owned;
    }
    return 0;
  });

  const getRankIcon = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `#${index + 1}`;
  };

  const getTrophyClass = (index) => {
    if (index === 0) return 'rank-gold';
    if (index === 1) return 'rank-silver';
    if (index === 2) return 'rank-bronze';
    return '';
  };

  const openFullscreenImage = (imageUrl, cardName) => {
    setFullscreenImage({ isOpen: true, imageUrl, cardName });
  };

  const closeFullscreenImage = () => {
    setFullscreenImage({ isOpen: false, imageUrl: '', cardName: '' });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Caricamento classifiche...</h2>
      </div>
    );
  }

  return (
    <div className="social-container">
      <div className="social-header">
        <h2>🏆 Classifica Fluidi</h2>
        <div className="sort-controls">
          <button 
            className={`sort-btn ${sortBy === 'cards' ? 'active' : ''}`}
            onClick={() => setSortBy('cards')}
          >
            🃏 Carte
          </button>
          <button 
            className={`sort-btn ${sortBy === 'patapizze' ? 'active' : ''}`}
            onClick={() => setSortBy('patapizze')}
          >
            🟡 Patapizze
          </button>
          <button 
            className={`sort-btn ${sortBy === 'spent' ? 'active' : ''}`}
            onClick={() => setSortBy('spent')}
          >
            💰 Spesi
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="rankings-grid">
        <div className="rankings-table">
          <p style={{ fontSize: '0.9rem', opacity: '0.8', marginBottom: '15px', fontStyle: 'italic' }}>
            💡 Clicca su una riga per vedere le carte del giocatore
          </p>
          <div className="table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Giocatore</th>
                  <th>🃏 Carte</th>
                  <th>🟡 Patapizze</th>
                  <th>💰 Spesi</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {sortedRankings.map((player, index) => (
                  <tr 
                    key={player.id} 
                    className={`${getTrophyClass(index)} ${player.id === user.id ? 'current-user' : ''} ${player.cards_owned > 0 ? 'clickable-row' : ''}`}
                    onClick={() => player.cards_owned > 0 && fetchUserCards(player.id)}
                    style={{ cursor: player.cards_owned > 0 ? 'pointer' : 'default' }}
                  >
                    <td className="rank-cell">
                      <span className="rank-badge">{getRankIcon(index)}</span>
                    </td>
                    <td className="player-cell">
                      <strong>{player.username}</strong>
                      {player.id === user.id && <span className="you-badge">TU</span>}
                      {player.cards_owned > 0 && <span className="clickable-indicator">👆</span>}
                    </td>
                    <td>{player.cards_owned}</td>
                    <td>
                      <span className="patapizza-icon"></span>
                      {player.patapizze.toLocaleString()}
                    </td>
                    <td>
                      <span className="patapizza-icon"></span>
                      {player.total_spent.toLocaleString()}
                    </td>
                    <td>
                      <button 
                        className="btn btn-view-cards"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents row click
                          fetchUserCards(player.id);
                        }}
                        disabled={player.cards_owned === 0}
                      >
                        {player.cards_owned > 0 ? '👀 Vedi Carte' : 'Nessuna'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {selectedUser && (
          <div className="user-collection">
            <div className="collection-header">
              <h3>🃏 Collezione di {selectedUser.username}</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedUser(null)}
              >
                ✕ Chiudi
              </button>
            </div>
            
            {userCards.length === 0 ? (
              <p className="no-cards">Questo giocatore non possiede ancora carte.</p>
            ) : (
              <div className="user-cards-grid">
                {userCards.map(card => {
                  const cardMetadata = card.metadata ? 
                    (typeof card.metadata === 'string' ? JSON.parse(card.metadata) : card.metadata) : {};
                  
                  return (
                    <div key={card.id} className="user-card-item">
                      {card.jpeg_filename && (
                        <img 
                          src={`${API_URL}/cards/${card.jpeg_filename}`}
                          alt={card.name}
                          className="user-card-image card-clickable"
                          onClick={() => openFullscreenImage(`${API_URL}/cards/${card.jpeg_filename}`, card.name)}
                        />
                      )}
                      <div className="card-info">
                        <h4>{card.name}</h4>
                        {cardMetadata.rarity && (
                          <p className="card-rarity">⭐ {translateRarity(cardMetadata.rarity)}</p>
                        )}
                        <p className="purchase-price">
                          Pagata: <span className="patapizza-icon"></span>{card.purchase_price?.toLocaleString() || '---'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      <FullscreenImageModal 
        isOpen={fullscreenImage.isOpen}
        imageUrl={fullscreenImage.imageUrl}
        cardName={fullscreenImage.cardName}
        onClose={closeFullscreenImage}
      />
    </div>
  );
}

export default Social;